// AdminBandDetailsPage.styles.js
import styled, { keyframes } from "styled-components";

const fadeInUp = keyframes`
  from { 
    transform: translateY(20px); 
    opacity: 0; 
  }
  to { 
    transform: translateY(0); 
    opacity: 1; 
  }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const PageContainer = styled.div`
  padding: 0;
  min-height: 100vh;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #333;
    border-top: 3px solid #ff6b35;
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
    margin: 0 auto 1rem;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

export const BackLink = styled.div`
  z-index: 100;
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  color: #ff6b35;
  font-weight: bold;
  padding: 10px 20px;
  border-radius: 25px;
  background: rgba(26, 26, 26, 0.8);
  border: 1px solid rgba(255, 107, 53, 0.3);
  text-decoration: none;
  width: fit-content;
  transition: 0.3s;
  &:hover {
    background: #ff6b35;
    color: #fff;
    transform: translateX(-5px);
  }
`;

export const PageTitle = styled.h1`
  color: #ff6b35;
  font-size: 2rem;
  font-weight: bold;
  margin: 2rem 0 0 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

export const BackButton = styled.button`
  background: linear-gradient(135deg, #333, #444);
  color: #ff6b35;
  border: 1px solid rgba(255, 107, 53, 0.3);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: linear-gradient(135deg, #444, #555);
    border-color: rgba(255, 107, 53, 0.5);
    transform: translateY(-2px);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }
`;

export const SaveButton = styled.button`
  background: ${({ $hasChanges }) =>
    $hasChanges
      ? "linear-gradient(135deg, #ff6b35, #ff8c42)"
      : "linear-gradient(135deg, #666, #777)"};
  color: #ffffff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: ${({ $hasChanges }) =>
    $hasChanges
      ? "0 4px 15px rgba(255, 107, 53, 0.3)"
      : "0 2px 8px rgba(0, 0, 0, 0.2)"};

  &:not(:disabled):hover {
    background: linear-gradient(135deg, #e55a2b, #e57342);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover:not(:disabled) svg {
    transform: rotate(15deg);
  }
`;

export const TabNavigation = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
  border-radius: 12px;
  padding: 0.5rem;
  overflow-x: auto;
  border: 1px solid rgba(255, 107, 53, 0.2);

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, #ff6b35, #ff8c42);
    border-radius: 2px;
  }
`;

export const TabButton = styled.button`
  background: ${({ $active }) =>
    $active ? "linear-gradient(135deg, #ff6b35, #ff8c42)" : "transparent"};
  color: ${({ $active }) => ($active ? "#ffffff" : "rgba(255, 255, 255, 0.7)")};
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(255, 107, 53, 0.5)" : "rgba(255, 255, 255, 0.1)"};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: ${({ $active }) => ($active ? "600" : "500")};
  font-size: 0.9rem;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(135deg, #e55a2b, #e57342)"
        : "rgba(255, 107, 53, 0.1)"};
    color: #ffffff;
    border-color: rgba(255, 107, 53, 0.4);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    padding: 0.6rem 0.8rem;
    font-size: 0.85rem;
  }
`;

export const ContentSection = styled.div`
  animation: ${fadeInUp} 0.5s ease-out;
`;

export const Card = styled.div`
  background: linear-gradient(135deg, #1a1a1a, #2a2a2a);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 107, 53, 0.2);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
`;

export const CardHeader = styled.div`
  background: linear-gradient(135deg, #2c2c2c, #1a1a1a);
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;

  h3 {
    color: #ff6b35;
    font-size: 1.3rem;
    font-weight: bold;
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;

    h3 {
      font-size: 1.1rem;
    }
  }
`;

export const CardContent = styled.div`
  padding: 2rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;

  .full-width {
    grid-column: 1 / -1;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FormLabel = styled.label`
  color: #ff6b35;
  font-weight: 600;
  font-size: 0.9rem;
`;

export const Relative = styled.div`
  position: relative;
  width: 100%;
`;

export const FormInput = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
  color: #ffffff;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  width: 100%;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }

  &[type="date"] {
    color-scheme: dark;
  }
`;

export const FormTextarea = styled.textarea`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
  color: #ffffff;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }
`;

export const FormSelect = styled.select`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
  color: #ffffff;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  cursor: pointer;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }

  option {
    background: #2a2a2a;
    color: #ffffff;
  }
`;

export const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  color: #ffffff;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: linear-gradient(135deg, #e55a2b, #e57342);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 53, 0.4);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }
`;

export const ItemCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: rgba(255, 107, 53, 0.3);
    background: rgba(255, 255, 255, 0.05);
  }

  .item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    h4 {
      color: #ffffff;
      font-size: 1.1rem;
      margin: 0;
    }

    .remove-btn {
      background: #e74c3c;
      color: #ffffff;
      border: none;
      padding: 0.5rem;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      transition: all 0.3s ease;

      &:hover {
        background: #c0392b;
        transform: scale(1.05);
      }
    }
  }
`;

// NEW TRACK MANAGEMENT STYLES
export const AlbumSection = styled.div`
  margin-bottom: 1.5rem;
`;

export const TracksSection = styled.div`
  .tracks-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding: 1rem 0;

    h5 {
      color: #ff6b35;
      font-size: 1.1rem;
      font-weight: 600;
      margin: 0;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }
  }
`;

export const SectionDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 107, 53, 0.3),
    transparent
  );
  margin: 1.5rem 0;
`;

export const TracksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const TrackItem = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 107, 53, 0.2);
    background: rgba(255, 255, 255, 0.04);
  }
`;

export const TrackGrid = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 120px 40px;
  gap: 1rem;
  align-items: end;

  .track-number {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;

    span {
      color: rgba(255, 107, 53, 0.8);
      font-weight: 600;
      font-size: 1rem;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;

    .track-number {
      order: -1;
      height: auto;
      justify-content: flex-start;

      span {
        font-size: 0.9rem;
      }
    }
  }
`;

export const TrackRemoveButton = styled.button`
  background: #e74c3c;
  color: #ffffff;
  border: none;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  height: 40px;
  width: 40px;

  &:hover {
    background: #c0392b;
    transform: scale(1.05);
  }

  svg {
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: rgba(255, 255, 255, 1);

  svg {
    color: rgba(255, 107, 53, 1);
    margin-bottom: 1rem;
  }

  h4 {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.95rem;
    line-height: 1.5;
    max-width: 400px;
    margin: 0 auto;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

export const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const GalleryItemCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(255, 107, 53, 0.3);
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const GalleryItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

export const GalleryItemControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  h4 {
    color: #ffffff;
    font-size: 1.1rem;
    margin: 0;
  }
`;

export const MainImageBadge = styled.span`
  background: linear-gradient(135deg, #ff6b35, #ff8c42);
  color: #ffffff;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const GalleryActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

export const MoveButton = styled.button`
  background: ${({ disabled }) => (disabled ? "#333" : "#444")};
  color: #ffffff;
  border: 1px solid ${({ disabled }) => (disabled ? "#444" : "#666")};
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  font-size: 0.9rem;
  font-weight: bold;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  transition: all 0.3s ease;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #555;
    border-color: #777;
  }
`;

export const GalleryRemoveButton = styled.button`
  background: #e74c3c;
  color: #ffffff;
  border: none;
  padding: 0.4rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #c0392b;
    transform: scale(1.05);
  }
`;

export const ImagePreview = styled.div`
  width: 100%;
  height: 200px;
  background: #1a1a1a;
  border-radius: 10px;
  margin-bottom: 1rem;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

export const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

export const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;

  svg {
    margin-bottom: 0.5rem;
    color: rgba(255, 107, 53, 0.5);
  }
`;

export const FileUploadInput = styled.input`
  background: rgba(255, 107, 53, 0.05);
  border: 2px dashed rgba(255, 107, 53, 0.3);
  border-radius: 10px;
  padding: 0.75rem;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    border-color: rgba(255, 107, 53, 0.5);
    background: rgba(255, 107, 53, 0.08);
  }

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ffffff;
  font-size: 0.9rem;
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #ff6b35;
    cursor: pointer;
  }

  &:hover {
    color: #ff6b35;
  }
`;

export const AchievementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const AchievementItem = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.3s ease;

  .achievement-inputs {
    display: flex;
    gap: 0.5rem;
    flex: 1;
    align-items: center;
  }

  &:hover {
    border-color: rgba(255, 107, 53, 0.3);
    background: rgba(255, 255, 255, 0.05);
  }
`;

export const AchievementInput = styled(FormInput)`
  flex: 1;
  margin: 0;
  margin-right: 0.5rem;
`;

export const AchievementYearInput = styled(FormInput)`
  width: 100px;
  margin: 0;
`;

export const AchievementRemoveButton = styled.button`
  background: #e74c3c;
  color: #ffffff;
  border: none;
  padding: 0.6rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  min-width: 40px;
  flex-shrink: 0;

  &:hover {
    background: #c0392b;
    transform: scale(1.05);
  }

  svg {
    font-size: 1rem;
  }
`;
